<script setup>
  import { computed, onMounted } from 'vue'
  import { useGoogleMapHelpers } from '@/composables/useGoogleMapHelpers';
  import { useRegionalNavigationStore } from '@/stores/RegionalNavigationStore';
  import { useSheetController } from '@/composables/useSheetsController';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import regionsData from '@/data/regionalNavigation.json';
  import MapRegionalNavigationPopup from '@/components/MapRegionalNavigationPopup.vue';
  import Icon from "@/components/icons/Icon.vue";

  const regionalNavigationStore = useRegionalNavigationStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');
  const { lineDistance } = useGoogleMapHelpers();
  
  const regionTree = ["continents", "countries", "regions", "subregions"];
  let flatRegions = [];

  const getDiv = () => window.$JWX.map.getDiv();

  const flattenregionsData = (regionsData) => {
    const flattenedregionsData = [];

    // Push in the world region
    flattenedregionsData.push({ name: 'World', lat: 0, lon: 0, zoom: 0 });

    if (Object.prototype.hasOwnProperty.call(regionsData, "continents")) {
        for (let i = 0; i < regionsData.continents.length; i++) {
            flattenedregionsData.push(copyRegion(regionsData.continents[i], [i]));

            if (regionsData.continents[i].countries) {
                for (let j = 0; j < regionsData.continents[i].countries.length; j++) {
                    flattenedregionsData.push(copyRegion(regionsData.continents[i].countries[j], [i, j]));

                    if (regionsData.continents[i].countries[j].regions) {
                        for (let k = 0; k < regionsData.continents[i].countries[j].regions.length; k++) {
                            flattenedregionsData.push(copyRegion(regionsData.continents[i].countries[j].regions[k], [i, j, k]));

                            if (regionsData.continents[i].countries[j].regions[k].subregions) {
                                for (let l = 0; l < regionsData.continents[i].countries[j].regions[k].subregions.length; l++) {
                                    flattenedregionsData.push(copyRegion(regionsData.continents[i].countries[j].regions[k].subregions[l], [i, j, k, l]));
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return flattenedregionsData;
  }

  const copyRegion = (region, position) => {
    const centerZoom = region.c.split(',');

    return {
      name: region.name,
      lat: parseFloat(centerZoom[0]),
      lon: parseFloat(centerZoom[1]),
      zoom: parseFloat(centerZoom[2]),
      position: position
    }
  }

  const overlapArea = (bounds1, bounds2) => {
    const xa1 = bounds1.getSouthWest().lng();
    const ya1 = bounds1.getSouthWest().lat();
    let xa2 = bounds1.getNorthEast().lng();
    const ya2 = bounds1.getNorthEast().lat();
    const xb1 = bounds2.getSouthWest().lng();
    const yb1 = bounds2.getSouthWest().lat();
    let xb2 = bounds2.getNorthEast().lng();
    const yb2 = bounds2.getNorthEast().lat();

    if (xa2 < xa1) xa2 += 360;
    if (xb2 < xb1) xb2 += 360;

    return Math.abs(Math.max(0, Math.min(xa2, xb2) - Math.max(xa1, xb1)) * Math.max(0, Math.min(ya2, yb2) - Math.max(ya1, yb1)));
  }

  const area = (bounds) => {
    const y1 = bounds.getSouthWest().lat();
    const x1 = bounds.getSouthWest().lng();
    const y2 = bounds.getNorthEast().lat();
    let x2 = bounds.getNorthEast().lng();

    if (x2 < x1) x2 += 360

    return Math.abs((x2 - x1) * (y2 - y1));
  }

  const boundsAt = (map, options) => {
    const defaultOptions = {
      zoom: null,
      center: null,
      div: map.getDiv(),
      projection: map.getProjection()
    }

    const { zoom, center, div, projection } = Object.assign({}, defaultOptions, options);

    if (!projection) return;

    const zf = Math.pow(2, zoom) * 2;
    const dw = div.offsetWidth / zf;
    const dh = div.offsetHeight / zf;
    const cpx = projection.fromLatLngToPoint(center || gMap.getCenter());

    return new google.maps.LatLngBounds(
      projection.fromPointToLatLng(new google.maps.Point(cpx.x - dw, cpx.y + dh)),
      projection.fromPointToLatLng(new google.maps.Point(cpx.x + dw, cpx.y - dh))
    );
  }

  const matchRegion = (map, minOverlap, flatRegions) => {
        const zoom = map.getZoom()
        const center = map.getCenter()
        const bounds = map.getBounds()

        const match = { index: 0, min_distance: 300, max_overlap: 1 }

        for (let i = 0; i < flatRegions.length; i++) {
          match.center = new google.maps.LatLng(flatRegions[i].lat, flatRegions[i].lon)
          match.distance = lineDistance(center, match.center)

          if (match.distance < match.min_distance && flatRegions[i].zoom <= zoom) {
            const matchBounds = boundsAt(map, { 
              zoom: flatRegions[i].zoom, 
              center: match.center, 
              div: getDiv() 
            })
            
            match.overlap = overlapArea(bounds, matchBounds) / area(bounds);

            if (match.overlap > minOverlap) {
              match.min_distance = match.distance
              match.max_overlap = match.overlap
              match.index = i
            }
          }
        }

        return {
          name: flatRegions[match.index].name,
          lat: flatRegions[match.index].lat,
          lon: flatRegions[match.index].lon,
          zoom: flatRegions[match.index].zoom,
          overlap: match.max_overlap,
          distance: match.min_distance,
          position: flatRegions[match.index].position
        }
  }

  const greaterRegions = computed(() => {
    if (!regionalNavigationStore.region.position) return;

    return regionalNavigationStore.region.path.map(pathRegion => pathRegion[0]).join(' > ');
  });

  const buildRegionData = () => {
    let currentRegionData = regionsData;
    regionalNavigationStore.region.path = [];

    if (!regionalNavigationStore.region.position) regionalNavigationStore.region.position = [0];
    
    for (let i = 0; i < regionalNavigationStore.region.position.length; i++) {
      const position = regionalNavigationStore.region.position[i];

      currentRegionData = getRegionData(currentRegionData, regionTree[i], position);

      if (i === regionalNavigationStore.region.position.length - 1) {
        const subRegionType = regionTree[i + 1];
        regionalNavigationStore.region.subRegions = subRegionType ? currentRegionData[subRegionType] : null;
      }

      if (i < regionalNavigationStore.region.position.length - 1) {
        regionalNavigationStore.region.path.push([currentRegionData.name, currentRegionData.c]);
      }
    }
  }

  const getRegionData = (data, regionType, position) => {
    return data[regionType][position]
  }

  const handleSubregionClick = (subRegion) => {
    regionalNavigationStore.changeRegion(subRegion);
  }

  const showRegionalNavigationSheet = () => {
    useSheetController.toggleSheet('config', 'regional-navigation');
  }

  onMounted(() => {
    flatRegions = flattenregionsData(regionsData);
    regionalNavigationStore.updateRegion(matchRegion(window.$JWX.map, 0.8, flatRegions));
    buildRegionData();

    google.maps.event.addListener(window.$JWX.map, 'idle', function () {
      regionalNavigationStore.updateRegion(matchRegion(window.$JWX.map, 0.8, flatRegions));
      buildRegionData();
    });
  })
</script>

<template>
  <div class="jwx-map__regional-navigation jwx-map-block jwx-map-block--transparent-border">
    <div class="jwx-map-block__content w-100" data-align="start">
      <div class="jwx-regional-navigation">
        <div class="jwx-regional-navigation__region">
          <h3 class="jwx-regional-navigation__name">{{ regionalNavigationStore.region.name }}</h3>
          <div v-if="regionalNavigationStore.region.position && regionalNavigationStore.region.position.length > 1" class="jwx-regional-navigation__greater-regions" v-html="greaterRegions"></div>
        </div>
        <MapRegionalNavigationPopup
          v-if="isDesktop"
          @subRegionClick="handleSubregionClick"
          :region="regionalNavigationStore.region"
        />
        <button
          v-else-if="regionalNavigationStore.region.subRegions"
          @click.prevent="showRegionalNavigationSheet"
          class="jwx-regional-navigation__sheet-button"
          data-size="small"
          data-icon-subtle="true"
        >
          <Icon name="chevron-up-down" class="jwx-regional-navigation__button-icon" />
        </button>
      </div>
    </div>
  </div>
</template>